/**
 * S-57 Chart Layer Configuration
 *
 * Each combination of layer filters maps to a dedicated cached MapProxy layer.
 * The frontend only sends the layer name (e.g. LAYERS=ENC_navigation_cables);
 * all OBJECT token logic lives server-side in the MapProxy config.
 */

/** Available S-57 filter layers that can be toggled on/off */
export const S57_LAYERS = [
  {
    id: 'navigation',
    label: 'Navigation',
    description: 'Buoys, beacons, lights, fairways, channels',
  },
  {
    id: 'cables',
    label: 'Cables & Infrastructure',
    description: 'Submarine cables, pipelines, wind farms, platforms',
  },
  {
    id: 'depths',
    label: 'Depths & Hazards',
    description: 'Soundings, depth areas, obstructions, wrecks',
  },
  {
    id: 'traffic',
    label: 'Traffic & Routing',
    description: 'TSS, traffic lanes, recommended routes, anchorages, berths',
  },
] as const;

export type S57LayerId = (typeof S57_LAYERS)[number]['id'];

/** Canonical order used to build the MapProxy layer name */
const LAYER_ORDER: S57LayerId[] = ['navigation', 'cables', 'depths', 'traffic'];

// --- Config shape (stored in user settings) ---

export interface S57Config {
  /** Empty array or all 4 selected = "All". Otherwise the selected subset. */
  selectedLayers: S57LayerId[];
}

export const DEFAULT_S57_CONFIG: S57Config = { selectedLayers: [] };

// --- Resolver ---

/**
 * Resolves an S57Config into the WMS LAYERS param value for MapProxy.
 * Returns 'ENC' for all (no filter), or 'ENC_navigation_cables' etc.
 */
export const resolveS57LayerName = (config: S57Config | undefined): string => {
  const { selectedLayers } = config ?? DEFAULT_S57_CONFIG;

  // No selection or all selected = show everything
  if (
    selectedLayers.length === 0 ||
    selectedLayers.length === LAYER_ORDER.length
  ) {
    return 'ENC';
  }

  // Sort by canonical order and build the layer name
  const sorted = LAYER_ORDER.filter((id) => selectedLayers.includes(id));
  return `ENC_${sorted.join('_')}`;
};
